// AIDEV-NOTE: Schemas Zod para o modulo de Pedidos (Sprint 4 - FEAT-004)
// Segue o padrao estabelecido em firebird.schemas.ts e crm.schemas.ts
import { z } from 'zod';

// --- Enums ---
export const StatusPedidoSchema = z.enum([
  'rascunho',
  'finalizado',
  'sincronizando',
  'sincronizado',
  'erro_sync'
]);
export type StatusPedido = z.infer<typeof StatusPedidoSchema>;

export const OrigemPedidoSchema = z.string();
export type OrigemPedido = z.infer<typeof OrigemPedidoSchema>;

// --- Tipos base ---
// AIDEV-NOTE: Renomeado para TabelaPrecoPedido para evitar colisao com TabelaPreco de firebird.schemas.ts
export const TabelaPrecoPedidoSchema = z.object({
  id: z.number().int().positive(),
  descricao: z.string(),
  codigo_firebird: z.number().int().positive().nullable().optional()
});
export type TabelaPrecoPedido = z.infer<typeof TabelaPrecoPedidoSchema>;

export const CondicaoPagtoSchema = z.object({
  id: z.number().int().positive(),
  descricao: z.string(),
  qtde_parcelas: z.number().int().positive(),
  acrescimo: z.number().nonnegative().default(0), // percentual
  desconto: z.number().nonnegative().default(0), // percentual
  parcelas: z.array(z.object({ dias: z.number().int().nonnegative() }))
});
export type CondicaoPagto = z.infer<typeof CondicaoPagtoSchema>;

export const ParcelaSchema = z.object({
  numero: z.number().int().positive(),
  vencimento: z.string().date(), // YYYY-MM-DD
  valor: z.number().positive()
});
export type Parcela = z.infer<typeof ParcelaSchema>;

export const PedidoItemSchema = z.object({
  id: z.string().uuid().optional(),
  pedido_id: z.string().uuid().optional(),
  produto_codigo: z.number().int().positive(),
  referencia: z.string(),
  descricao: z.string(),
  cor: z.string().nullable().optional(),
  tamanho: z.string().nullable().optional(),
  quantidade: z.number().int().positive(),
  valor_unitario: z.number().positive(),
  desconto_percentual: z.number().nonnegative().default(0),
  total_item: z.number().positive(),
  origem_estoque: z.string().nullable().optional(),
  // AIDEV-NOTE: codcor/codtamanho gravados no item para uso direto no Firebird (COLECAO, TAMANHO)
  codcor: z.number().int().nullable().optional(),
  codtamanho: z.number().int().nullable().optional(),
  // AIDEV-NOTE: quantidade_romaneada/faturada sincronizadas de volta do Firebird apos sync
  quantidade_romaneada: z.number().int().nonnegative().nullable().optional(),
  quantidade_faturada: z.number().int().nonnegative().nullable().optional()
});
export type PedidoItem = z.infer<typeof PedidoItemSchema>;

export const PedidoSchema = z.object({
  id: z.string().uuid(),
  client_id: z.number().int().positive(),
  vendedor_id: z.number().int().positive(),
  cliente_codigo: z.number().int().positive(),
  cliente_nome: z.string(),
  cliente_cpf_cnpj: z.string().nullable().optional(),
  // AIDEV-NOTE: limite de desconto especial (politicas_cliente) do cliente do pedido — reidratado
  // pelo findById via LEFT JOIN para que a edicao de rascunho preserve o limite no Step3
  limite_desconto_especial: z.number().nullable().optional(),
  numero: z.number().int().positive().nullable().optional(), // atribuido apos sync com Firebird
  status: StatusPedidoSchema,
  origem: OrigemPedidoSchema.default('app'),
  tabela_preco_id: z.number().int().positive().nullable().optional(),
  condicao_pagto_id: z.number().int().positive().nullable().optional(),
  desconto_geral_percentual: z.number().nonnegative().default(0),
  observacao: z.string().nullable().optional(),
  total_produtos: z.number().nonnegative(),
  total_desconto: z.number().nonnegative().default(0),
  total_final: z.number().nonnegative(),
  itens: z.array(PedidoItemSchema).default([]),
  parcelas: z.array(ParcelaSchema).default([]),
  sync_attempts: z.number().int().nonnegative().default(0),
  sync_error: z.string().nullable().optional(),
  // AIDEV-NOTE: revision otimista do rascunho (Task 7) — incrementada a cada PATCH /itens
  // aceito; EditarPedidoPage usa para popular o revisionRef do wizard
  revision: z.number().int().nonnegative().default(0).optional(),
  // AIDEV-NOTE: campos sync com Firebird — salvos na finalizacao do pedido
  tipo_comercializacao_id: z.number().int().positive().nullable().optional(),
  tipo_movimento_receber_id: z.number().int().positive().nullable().optional(),
  // AIDEV-NOTE: id_pedido_integracao garante idempotencia no Firebird (CARTEIRA_PEDIDO.ID_PEDIDO_INTEGRACAO)
  id_pedido_integracao: z.string().uuid().nullable().optional(),
  // AIDEV-NOTE: data_pedido_erp e hora_pedido_erp gravados apos sync bem-sucedido com Firebird
  data_pedido_erp: z.string().nullable().optional(),
  hora_pedido_erp: z.string().nullable().optional(),
  // AIDEV-NOTE: status_erp sincronizado de volta do Firebird (P=Em análise, A=Aprovado, R=Romaneado, F=Faturado, C=Cancelado)
  status_erp: z.enum(['P', 'A', 'R', 'F', 'C']).nullable().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional()
});
export type Pedido = z.infer<typeof PedidoSchema>;

// --- Tipos auxiliares de calculo (client-only, mas compartilhados para testes) ---
export const PrecoCalculadoSchema = z.object({
  preco_unitario: z.number().positive(),
  subtotal: z.number().nonnegative(),
  modo_aplicado: z.enum(['varejo', 'atacado', 'promocional', 'tabela', 'calculado']),
  is_promocional: z.boolean().default(false)
});
export type PrecoCalculado = z.infer<typeof PrecoCalculadoSchema>;

// --- Zod schemas para API (request/response) ---
export const createPedidoSchema = z.object({
  // AIDEV-NOTE: id opcional gerado pelo client — pedido criado offline mantem o mesmo UUID no server
  id: z.string().uuid().optional(),
  // AIDEV-NOTE: Códigos negativos (-1, -2, ...) são provisórios para clientes criados no app antes do sync com Firebird
  cliente_codigo: z.number().int(),
  cliente_nome: z.string().min(1),
  cliente_cpf_cnpj: z.string().nullable().optional(),
  tabela_preco_id: z.number().int().positive().nullable().optional(),
  origem: OrigemPedidoSchema.optional()
});

export const addItemSchema = z.object({
  produto_codigo: z.number().int().positive(),
  referencia: z.string().min(1),
  descricao: z.string().min(1),
  cor: z.string().nullable().optional(),
  tamanho: z.string().nullable().optional(),
  quantidade: z.number().int().positive(),
  valor_unitario: z.number().positive(),
  desconto_percentual: z.number().nonnegative().max(100).default(0),
  origem_estoque: z.string().nullable().optional(),
  // AIDEV-NOTE: custo e codbarras vindos do sync_produtos — gravados nos itens para rastreabilidade
  custo: z.number().nonnegative().nullable().optional(),
  codbarras: z.string().nullable().optional(),
  // AIDEV-NOTE: codcor e codtamanho vindos do sync_produtos — gravados para sync direto ao Firebird
  // (CARTEIRA_ITENS_PEDIDO.COLECAO = codcor, CARTEIRA_ITENS_PEDIDO.TAMANHO = codtamanho)
  codcor: z.number().int().nullable().optional(),
  codtamanho: z.number().int().nullable().optional()
});

export const finalizarPedidoSchema = z.object({
  condicao_pagto_id: z.number().int().positive(),
  desconto_geral_percentual: z.number().nonnegative().max(100).optional(),
  acrescimo_geral_percentual: z.number().nonnegative().max(100).optional(),
  observacao: z.string().max(500).optional(),
  // AIDEV-NOTE: origem e selecionada no Step4 e persistida aqui na finalizacao
  origem: z.string().optional(),
  // AIDEV-NOTE: tipo_comercializacao e tipo_movimento_receber — opcionais, usam padrão das políticas se null
  tipo_comercializacao_id: z.number().int().positive().nullable().optional(),
  tipo_movimento_receber_id: z.number().int().positive().nullable().optional(),
  itens: z.array(addItemSchema).min(1, 'Pedido deve ter ao menos 1 item'),
  parcelas: z.array(ParcelaSchema).min(1, 'Pedido deve ter ao menos 1 parcela'),
  // AIDEV-NOTE: confirmacao explicita quando o total cai >50% vs rascunho salvo (guarda de regressao)
  confirmar_reducao: z.boolean().optional()
});

// AIDEV-NOTE: Schema para auto-save de itens do rascunho (PATCH /pedidos/:id/itens)
// Permite salvar itens parcialmente sem finalizar o pedido
export const saveItensRascunhoSchema = z.object({
  itens: z.array(addItemSchema),
  // AIDEV-NOTE: revision opcional p/ retrocompat com clients antigos — sem ela o server
  // aplica sem checagem (comportamento legado)
  revision: z.number().int().nonnegative().optional()
});

export type CreatePedidoDto = z.infer<typeof createPedidoSchema>;
export type AddItemDto = z.infer<typeof addItemSchema>;
export type FinalizarPedidoDto = z.infer<typeof finalizarPedidoSchema>;
export type SaveItensRascunhoDto = z.infer<typeof saveItensRascunhoSchema>;
